import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { linkWithQuery, objectMerge, resolveParams, resolveState, deepEqual } from '@labradorsports/utils';

import { RootState, routerActions, RouterState } from '../../store/index.js';
import { baseParamState, paramSetup, usedQueryParams } from '../constants.js';
import { useDispatcher } from '../hooks/index.js';

function resolveAllParams(pathname: string, state: RouterState, currentParams: URLSearchParams) {
    const resolvedParams = resolveParams(paramSetup, pathname, state);

    const outsiderParams = Array.from(currentParams.entries()).filter(([p]) => !usedQueryParams.includes(p));
    outsiderParams.forEach(([key, value]) => {
        resolvedParams.set(key, value);
    });

    return resolvedParams;
}

export interface RouterContextValue {
    navigateWithQuery: (to: string | number, stateMods?: Partial<RouterState>, options?: { replace?: boolean }) => void;
}

export const RouterContext = createContext<RouterContextValue>(null);

export const RouterStateProvider: FC = ({ children }) => {
    const navigate = useNavigate();
    const updateState = useDispatcher(routerActions.UpdateRouterState);
    const state = useSelector((state: RootState) => state.router);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const prevResolvedParams = useRef(searchParams);
    const skipLocationUpdate = useRef(true);

    useEffect(() => {
        // Skip location update when state update was caused by previous location update
        if (skipLocationUpdate.current) {
            skipLocationUpdate.current = false;
            return;
        }

        const resolvedParams = resolveAllParams(location.pathname, state, searchParams);

        // If params have changed, trigger a location update
        if (resolvedParams.toString() !== prevResolvedParams.current?.toString()) {
            setSearchParams(resolvedParams);
        }

        prevResolvedParams.current = resolvedParams;
    }, [state]);

    useEffect(() => {
        const paramState = resolveState(paramSetup, location.pathname, searchParams);
        const nextRouterState = objectMerge(baseParamState, paramState);

        if (!deepEqual(nextRouterState, state)) {
            updateState(nextRouterState);
        }
        // Intentionally only when the URL changes; router may hold in-URL state that should not be reset on other updates.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sync URL → router slice when location changes
    }, [location]);

    // TODO: do we need the individual router state setters anymore?
    const navigateWithQuery = useCallback(
        (to: string | number, stateMods?: Partial<RouterState>, { replace = false } = {}) => {
            // Handle back/forward navigation
            if (typeof to === 'number') {
                navigate(to);
                return;
            }

            if (location.pathname === to && !stateMods) {
                return;
            }

            skipLocationUpdate.current = true;

            const finalState = {
                ...state,
                ...(stateMods ?? {}),
            };

            if (stateMods && !deepEqual(finalState, state)) {
                updateState(finalState);
            }

            const combinedSearchParams = resolveAllParams(to, finalState, searchParams);

            navigate(linkWithQuery(to, combinedSearchParams), { replace });
        },
        [searchParams, location.pathname, state]
    );

    return (
        <RouterContext.Provider
            value={{
                navigateWithQuery,
            }}
        >
            {children}
        </RouterContext.Provider>
    );
};

export const useNavigateWithQuery = () => {
    const { navigateWithQuery } = useContext(RouterContext);

    return navigateWithQuery;
};
